import React, { useEffect, useMemo, useState } from 'react'
import { Location, SampleUser } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import DatePicker from '../../../componants/datePicker/DatePicker'
import './myGroups.css'

// Wired to GET /splitwise/groups/. Location badge now reads the real `tag`
// field. The unread-count badge still has no backing field at all — hidden
// per-card when absent. Chat preview line is backed by `last_message`, an
// object per the API doc, not a plain string — see formatLastMessage.
const TABS = [
    { key: 'all', label: 'All Groups' },
    { key: 'active', label: 'Active' },
    { key: 'archive', label: 'Archived' },
    { key: 'settled', label: 'Settled' },
    { key: 'unsettled', label: 'Unsettled' },
]

const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <circle cx="7" cy="7" r="4.5" stroke="#9CA3AF" strokeWidth="1.4" />
        <path d="M13 13 10.5 10.5" stroke="#9CA3AF" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
)

const THEME_BACKGROUNDS = [
    'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
    'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)',
    'linear-gradient(135deg, #86EFAC 0%, #4ADE80 100%)',
    'linear-gradient(135deg, #FCD34D 0%, #FBBF24 100%)',
]

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M11.5 3.5 5.5 10.5 2.5 7.5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

// last_message is an object ({sender_name, message}) or null, per the API
// doc's own rule — never a plain string, so it can't be rendered directly.
const formatLastMessage = (lastMessage) => {
    if (!lastMessage) return ''
    const { sender_name, message } = lastMessage
    if (!message) return ''
    return sender_name ? `${sender_name}: ${message}` : message
}

// "10:30 AM" today, "Yesterday", "N days ago", "N weeks ago" — group list
// only gives raw timestamps, no pre-formatted relative label.
const formatRelativeTime = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const now = new Date()
    if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
    }
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    const diffWeeks = Math.floor(diffDays / 7)
    return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`
}

const GroupAvatarStack = ({ avatars = [], count }) => {
    const shown = Math.min(count, 3)
    const extra = count - shown
    return (
        <div className='groupAvatarStack'>
            {Array.from({ length: shown }).map((_, index) => (
                <img key={index} src={avatars[index] || SampleUser} alt="" className='groupAvatarStack__avatar' />
            ))}
            {extra > 0 && <span className='groupAvatarStack__more'>+{extra}</span>}
        </div>
    )
}

const GroupStatus = ({ group }) => {
    if (group.status === 'settled') {
        return (
            <div className='groupCard__statusCopy'>
                <p className='groupCard__settled'><CheckIcon /> All Settled</p>
                <p className='groupCard__subStatus'>{group.time}</p>
            </div>
        )
    }
    if (group.status === 'owe') {
        return (
            <>
                <div className='groupCard__statusCopy'>
                    <p className='groupCard__owe'>You owe ₹{group.oweAmount}</p>
                    <p className='groupCard__subStatus'>Unsettled</p>
                    <p className='groupCard__subStatus'>{group.time}</p>
                </div>
                {group.badge ? <span className='groupCard__badge'>{group.badge}</span> : null}
            </>
        )
    }
    if (group.status === 'owedToYou') {
        return (
            <div className='groupCard__statusCopy'>
                <p className='groupCard__settled'>₹{group.owedAmount} owed to you</p>
                <p className='groupCard__subStatus'>{group.time}</p>
            </div>
        )
    }
    return (
        <div className='groupCard__statusCopy'>
            <p className='groupCard__planning'>Planning Stage</p>
            <p className='groupCard__subStatus'>{group.time}</p>
        </div>
    )
}

const MyGroups = ({ onCreateGroup, onOpenGroup }) => {
    const [activeTab, setActiveTab] = useState(TABS[0].key)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [groups, setGroups] = useState([])
    const [loading, setLoading] = useState(true)

    // Avoid firing a request on every keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        setLoading(true)
        const params = { filter: activeTab }
        if (debouncedSearch) params.search = debouncedSearch
        if (fromDate) params.from_date = fromDate
        if (toDate) params.to_date = toDate
        service
            .get(endpoints.splitwise.groups, params, { silent: true })
            .then(({ data }) => setGroups(Array.isArray(data.data) ? data.data : []))
            .catch(() => setGroups([]))
            .finally(() => setLoading(false))
    }, [activeTab, debouncedSearch, fromDate, toDate])

    const cards = useMemo(() => groups.map((group, index) => {
        const youOwe = Number(group.you_owe) || 0
        const owedToYou = Number(group.owed_to_you) || 0
        const status = group.settlement_status === 'settled' ? 'settled'
            : youOwe > 0 ? 'owe'
                : owedToYou > 0 ? 'owedToYou'
                    : Number(group.total_spend) === 0 ? 'planning'
                        : 'settled'
        return {
            id: group.id,
            name: group.name,
            location: group.tag || '',
            preview: formatLastMessage(group.last_message),
            icon: group.icon || '',
            theme: THEME_BACKGROUNDS[index % THEME_BACKGROUNDS.length],
            members: group.member_count ?? (group.members?.length || 0),
            avatars: (group.members || []).map((m) => m.image).filter(Boolean),
            status,
            oweAmount: youOwe.toLocaleString('en-IN'),
            owedAmount: owedToYou.toLocaleString('en-IN'),
            badge: group.unread_count || null, // not returned by the API yet
            time: formatRelativeTime(group.updated_at || group.created_at),
        }
    }), [groups])

    return (
        <div>
            <div className='myGroupsHeader'>
                <div>
                    <div className='myGroupsHeader__title'>My Groups</div>
                    <div className='myGroupsHeader__subtitle'>Manage and split expenses with your travel buddies</div>
                </div>
                <button type='button' className='commonBtn' onClick={onCreateGroup}>+ Create New Group</button>
            </div>

            <div className='myGroupsToolbar'>
                <div className='myGroupsTabs'>
                    {TABS.map((tab) => (
                        <div
                            key={tab.key}
                            className={`myGroupsTab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>

                <div className='myGroupsFilters'>
                    <div className='myGroupsSearch'>
                        <span className='myGroupsSearch__icon'><SearchIcon /></span>
                        <input
                            type='text'
                            placeholder='Search groups'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='myGroupsDateField'>
                        <DatePicker value={fromDate} onChange={setFromDate} max={toDate || undefined} placeholder='From' />
                    </div>
                    <div className='myGroupsDateField'>
                        <DatePicker value={toDate} onChange={setToDate} min={fromDate || undefined} placeholder='To' />
                    </div>
                </div>
            </div>

            <div className='myGroupsList'>
                {loading ? (
                    <p className='bsFareNote'>Loading groups...</p>
                ) : cards.length ? cards.map((group) => (
                    <div className='groupCard' key={group.id} onClick={() => onOpenGroup?.(group.id)}>
                        <span className='groupCard__icon' style={{ background: group.icon ? '#fff' : group.theme }}>
                            {group.icon ? (
                                <img src={group.icon} alt="" className='groupCard__iconImage' />
                            ) : (
                                <img src={Location} alt="" />
                            )}
                        </span>

                        <div className='groupCard__info'>
                            <div className='groupCard__nameRow'>
                                <span className='groupCard__name'>{group.name}</span>
                                {group.location ? <span className='groupCard__location'>{group.location}</span> : null}
                            </div>
                            {group.preview ? <div className='groupCard__preview'>{group.preview}</div> : null}
                        </div>

                        <div className='groupCard__members'>
                            <GroupAvatarStack avatars={group.avatars} count={group.members} />
                            <span className='groupCard__membersLabel'>{group.members} members</span>
                        </div>

                        <div className='groupCard__status'>
                            <GroupStatus group={group} />
                        </div>
                    </div>
                )) : (
                    <p className='bsFareNote'>No groups yet. Create one to start splitting expenses.</p>
                )}
            </div>
        </div>
    )
}

export default MyGroups
